import { Box, Grid, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';

import MuiDialog from 'components/MuiDialog';
import {
  SurveyDialogProps,
  TDebriefingDefaultValues,
} from 'routes/Main/Survey/type';
import useSurvey from 'store/survey/useSurvey';
import useNotification from 'hooks/useNotification';

import TextareaSection from '../../components/TextAreaSection';

import { updateDebriefing } from 'apis/survey';
import PatientInfo from './PatientInfo';
import UserInfo from './UserInfo';

const Debriefing = (props: SurveyDialogProps<TDebriefingDefaultValues>) => {
  const {
    title,
    isOpen,
    disabled,
    defaultValues,
    user_id,
    nurseName,
    patientInfo,
    onClose,
  } = props;

  const { onUpdateIsSave } = useSurvey();
  const { onSuccess, onFail, onResultCode, onRequired } = useNotification();

  const { handleSubmit, register, getValues, setValue, watch } = useForm({
    defaultValues,
  });

  const onSubmit = (data: TDebriefingDefaultValues) => {
    const request = {
      user_id,
      patient_id: patientInfo.patient_id,
      debriefing_survey: { ...data },
    };

    updateDebriefing(request)
      .then(({ data: { rc } }) => {
        if (rc !== 1) return onResultCode(rc);

        onUpdateIsSave(true);
        onSuccess('저장되었습니다');
      })
      .catch(e => onFail('저장에 실패하였습니다', e));
  };

  const formProps = {
    disabled,
    watch,
    register,
    getValues,
    setValue,
    onSuccess,
    onRequired,
  };

  return (
    <MuiDialog.SurveyForm
      title={title}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={disabled ? undefined : handleSubmit(onSubmit)}
      update_at={defaultValues?.update_at}
    >
      <Grid
        container
        wrap="wrap"
        rowSpacing={5}
        columnSpacing={3}
        sx={{ py: 5, px: 1 }}
      >
        <Typography
          sx={{
            margin: '40px auto 0px auto',
            fontWeight: '700',
            fontSize: '16px',
            textAlign: 'center',
          }}
        >
          디브리핑 기록지
        </Typography>
        <PatientInfo patientInfo={patientInfo} nurseName={nurseName} />
        <UserInfo
          {...formProps}
          patientInfo={patientInfo}
          nurseName={nurseName}
        />
        <TextareaSection
          {...formProps}
          title="1. 대상자는 어떤 상황이었나요? 어떤 점이 가장 중요한 간호 문제라고 생각하나요?"
          registerId="one"
          required={false}
          preventCopyPaste={true} // 복붙 금지 활성화
        />
        <TextareaSection
          {...formProps}
          title="2. 우선적인 간호문제 이외에 다른 간호문제는 무엇이었나요?"
          registerId="two"
          required={false}
          preventCopyPaste={true}
        />
        <TextareaSection
          {...formProps}
          title="3. 대상자의 건강문제를 파악했을 때 (증상을 호소했을 때), 가장 우선적으로 수행할 간호 활동은 무엇이라고 판단했나요? 그 근거는 무엇이었나요?"
          registerId="three"
          required={false}
          preventCopyPaste={true}
        />
        <TextareaSection
          {...formProps}
          title="4. 핵심간호술 프로토콜을 하나씩 살펴보며, 내가 수행한 종합수행을 떠올려 보세요. 잘 수행한 부분은 어디인가요? 반대로 누락, 지연, 오류는 어느 부분에서 있었나요?"
          registerId="four"
          required={false}
          preventCopyPaste={true}
        />
        <TextareaSection
          {...formProps}
          title="5. 잘 수행하지 못한 부분을 교정하기 위해서 어떤 방법(전략)을 사용할 수 있을까요?"
          registerId="five"
          required={false}
          preventCopyPaste={true}
        />
        <TextareaSection
          {...formProps}
          title="6. 오늘 배운 것 중에서 가장 중요한 것 한 가지를 꼽으라면? 왜 그렇게 생각하나요?"
          registerId="six"
          required={false}
          preventCopyPaste={true}
        />
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'right' }}>
        <Typography sx={{ color: '#14855B', fontSize: '12px' }}>
          * 해당 기록지는 차의과학대학교 간호학과 협조를 통해 제작한
          기록지입니다.
        </Typography>
      </Box>
    </MuiDialog.SurveyForm>
  );
};

export default Debriefing;
